import { useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Loader2, RefreshCw, Save, GripVertical, ChevronDown, ChevronUp, Pencil, X, Check } from "lucide-react";

const SECTION_META = {
  executive_snapshot: { label: "Executive Snapshot", editable: true },
  revenue_leak_analysis: { label: "Revenue Leak Analysis", editable: true },
  financial_impact: { label: "Financial Impact Model", editable: true },
  diagnosis: { label: "Current State Diagnosis", editable: true },
  opportunity_matrix: { label: "AI Opportunity Matrix", editable: true },
  department_transformation: { label: "Department Transformation", editable: true },
  strategy: { label: "AI Strategy", editable: true },
  implementation_roadmap: { label: "Implementation Roadmap", editable: true },
  roi_model: { label: "ROI Model", editable: true },
  outcomes: { label: "Expected Outcomes", editable: true },
  risk_of_inaction: { label: "Risk of Inaction", editable: true },
  implementation_path: { label: "Implementation Path", editable: true },
  system_designs: { label: "System Designs", editable: true },
  agent_architecture: { label: "Agent Architecture", editable: true },
  tool_stack: { label: "Tool Stack", editable: true },
};

const DEFAULT_ORDER = [
  "executive_snapshot", "revenue_leak_analysis", "financial_impact", "diagnosis",
  "opportunity_matrix", "department_transformation", "strategy",
  "implementation_roadmap", "roi_model", "outcomes", "risk_of_inaction",
  "implementation_path", "system_designs", "agent_architecture", "tool_stack",
];

export default function ReportEditor({ auditId, report, onUpdate }) {
  const [editingKey, setEditingKey] = useState(null);
  const [editJson, setEditJson] = useState("");
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const sectionOrder = report?.section_order || DEFAULT_ORDER;
  const activeSections = sectionOrder.filter(k => report?.[k] && SECTION_META[k]);

  const startEdit = (key) => {
    setEditingKey(key);
    setEditJson(JSON.stringify(report[key], null, 2));
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditJson("");
  };

  const saveSection = async () => {
    if (!editingKey) return;
    setSaving(true);
    try {
      const parsed = JSON.parse(editJson);
      await api.put(`/report/${auditId}/section/${editingKey}`, { content: parsed });
      onUpdate?.();
      setEditingKey(null);
      setEditJson("");
    } catch (e) {
      alert(e.message || "Invalid JSON or save failed");
    }
    finally { setSaving(false); }
  };

  const regenerateSection = async (key) => {
    if (!window.confirm(`Regenerate "${SECTION_META[key]?.label}" with AI? This will replace the current content.`)) return;
    setRegenerating(key);
    try {
      await api.post(`/report/${auditId}/section/${key}/regenerate`);
      onUpdate?.();
    } catch (e) {
      console.error("Regenerate failed:", e);
      alert("Regeneration failed");
    }
    finally { setRegenerating(null); }
  };

  const moveSection = async (key, direction) => {
    const idx = activeSections.indexOf(key);
    if (idx < 0) return;
    const newIdx = direction === "up" ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= activeSections.length) return;
    const newOrder = [...activeSections];
    [newOrder[idx], newOrder[newIdx]] = [newOrder[newIdx], newOrder[idx]];
    try {
      await api.put(`/report/${auditId}/reorder`, { order: newOrder });
      onUpdate?.();
    } catch (e) { console.error("Reorder failed:", e); }
  };

  const renderPreview = (key, data) => {
    if (!data) return <span className="text-xs text-slate-400 italic">Empty</span>;
    if (typeof data === "string") return <p className="text-xs text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed line-clamp-3">{data}</p>;
    if (Array.isArray(data)) return <p className="text-xs text-slate-400">{data.length} items</p>;
    if (typeof data === "object") {
      const keys = Object.keys(data);
      return <p className="text-xs text-slate-400 font-['JetBrains_Mono']">{keys.slice(0, 4).join(", ")}{keys.length > 4 ? ` +${keys.length - 4} more` : ""}</p>;
    }
    return <span className="text-xs text-slate-600">{String(data)}</span>;
  };

  return (
    <div className="space-y-2" data-testid="report-editor">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A]">Report Sections</h3>
          <p className="text-xs text-slate-400 font-['IBM_Plex_Sans']">{activeSections.length} sections. Click to edit, drag to reorder, or regenerate with AI.</p>
        </div>
      </div>

      {activeSections.map((key, idx) => {
        const meta = SECTION_META[key];
        if (!meta) return null;
        const data = report[key];
        const isEditing = editingKey === key;
        const isExpanded = expanded === key;
        const isRegenerating = regenerating === key;

        return (
          <div key={key} className={`bg-white border ${isEditing ? "border-[#0033CC]" : "border-slate-200"}`} data-testid={`section-${key}`}>
            <div className="flex items-center gap-2 p-3 cursor-pointer" onClick={() => setExpanded(isExpanded ? null : key)}>
              <GripVertical className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
              <span className="text-xs font-['JetBrains_Mono'] text-slate-400 w-6 text-center">{String(idx + 1).padStart(2, "0")}</span>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-['Outfit'] font-medium text-[#0F172A]">{meta.label}</span>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={(e) => { e.stopPropagation(); moveSection(key, "up"); }} disabled={idx === 0} className="p-1 text-slate-400 hover:text-[#0033CC] disabled:opacity-30" data-testid={`move-up-${key}`}>
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <button onClick={(e) => { e.stopPropagation(); moveSection(key, "down"); }} disabled={idx === activeSections.length - 1} className="p-1 text-slate-400 hover:text-[#0033CC] disabled:opacity-30" data-testid={`move-down-${key}`}>
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                <button onClick={(e) => { e.stopPropagation(); startEdit(key); }} className="p-1 text-slate-400 hover:text-[#0033CC]" data-testid={`edit-${key}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={(e) => { e.stopPropagation(); regenerateSection(key); }} disabled={isRegenerating} className="p-1 text-slate-400 hover:text-[#0033CC]" data-testid={`regen-${key}`}>
                  {isRegenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>

            {/* Preview (collapsed) */}
            {isExpanded && !isEditing && (
              <div className="px-3 pb-3 ml-12">
                {renderPreview(key, data)}
              </div>
            )}

            {/* Editor */}
            {isEditing && (
              <div className="p-3 border-t border-slate-200 bg-slate-50" data-testid={`editor-${key}`}>
                <Textarea
                  value={editJson}
                  onChange={(e) => setEditJson(e.target.value)}
                  className="rounded-none border-slate-300 text-xs font-mono min-h-[200px] mb-2"
                  data-testid={`json-editor-${key}`}
                />
                <div className="flex gap-2 justify-end">
                  <Button onClick={cancelEdit} variant="outline" size="sm" className="rounded-none text-xs font-['Outfit']">
                    <X className="w-3 h-3 mr-1" />Cancel
                  </Button>
                  <Button onClick={saveSection} disabled={saving} size="sm" className="rounded-none bg-[#0033CC] text-white text-xs font-['Outfit']" data-testid={`save-${key}`}>
                    {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}Save
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
